<template>
  <div class="group-page">
    <van-nav-bar fixed title="拼单详情" left-text="返回" left-arrow @click-left="goBack" />

    <div v-if="loading" class="center-state">
      <van-loading size="24">加载中...</van-loading>
    </div>

    <template v-else-if="detail">
      <!-- 状态横幅 -->
      <div class="status-banner" :class="'s' + detail.group.status">
        <div class="sb-left">
          <div class="sb-title">{{ detail.group.status_text }}</div>
          <div class="sb-sub" v-if="detail.group.status === 0">还差 {{ detail.group.remain_num }} 人成团</div>
          <div class="sb-sub" v-else-if="detail.group.status === 1">两个订单已更新为待发货</div>
          <div class="sb-sub" v-else>未成团，已支付用户可到订单中申请退款</div>
        </div>
        <div class="sb-countdown" v-if="detail.group.status === 0">{{ countdownText }}</div>
      </div>

      <!-- 商品卡片 -->
      <div class="goods-card" @click="$router.push(`/prodetail/${detail.goods.goods_id}`)">
        <img :src="detail.goods.goods_image" class="g-img" alt="" />
        <div class="g-info">
          <div class="g-name text-ellipsis-2">{{ detail.goods.goods_name }}</div>
          <div class="g-price">
            <span class="group-price">拼单价 ¥{{ detail.group.group_price }}</span>
            <span class="origin-price">单买 ¥{{ detail.goods.goods_price_min }}</span>
          </div>
        </div>
      </div>

      <!-- 拼团进度 -->
      <div class="progress-card">
        <div class="p-tit">
          <span class="p-label">{{ detail.group.leader_nickname }} 发起的拼单</span>
          <span class="p-num">{{ detail.group.joined_num }}/{{ detail.group.total_num }}人</span>
        </div>
        <van-progress
          :percentage="progressPercent"
          :show-pivot="false"
          color="#ff6b35"
          track-color="#ffece3"
          stroke-width="8"
        />
        <div class="members">
          <div class="member" v-for="(m, i) in memberList" :key="i">
            <img :src="m.avatar || defaultAvatar" class="m-avatar" alt="" />
            <span class="m-name">{{ m.nickname }}</span>
          </div>
        </div>
      </div>

      <!-- 操作按钮：主操作整行 + 下方 分享邀请 / 联系客服 / 单独购买 同一行 -->
      <div class="action-bar">
        <button
          v-if="detail.group.status === 0 && !detail.me.joined"
          class="btn primary"
          @click="joinGroup"
        >我要参团 ¥{{ detail.group.group_price }}</button>
        <button
          v-else-if="detail.group.status === 0 && detail.me.joined && isMyOrderUnpaid"
          class="btn primary"
          @click="goPay"
        >去支付（¥{{ detail.me.my_order.total_price }}）</button>
        <button v-else-if="detail.group.status === 1" class="btn done" disabled>拼单成功</button>
        <button v-else-if="detail.group.status !== 0" class="btn done" disabled>拼单已结束</button>

        <div class="action-sub-row">
          <button
            v-if="detail.group.status === 0 && detail.me.joined && !isMyOrderUnpaid"
            class="btn share"
            @click="share"
          >分享邀请</button>
          <button class="btn service" @click="goService">联系客服</button>
          <button class="btn plain" @click="$router.push(`/prodetail/${detail.goods.goods_id}`)">单独购买</button>
        </div>
      </div>
    </template>

    <div v-else class="center-state">
      <span class="err-icon">😕</span>
      <p>{{ errorText }}</p>
    </div>

    <!-- 分享弹窗 -->
    <van-dialog v-model="shareVisible" title="邀请好友拼单" :show-confirm-button="false" close-on-click-overlay>
      <div class="share-box">
        <div class="share-link">{{ shareUrl }}</div>
        <div class="share-actions">
          <button class="share-btn" @click="copyLink">复制链接</button>
          <button class="share-btn" @click="toWechatShare">微信转发</button>
        </div>
        <img v-if="qrDataUrl" :src="qrDataUrl" class="share-qr" alt="二维码" />
        <p class="share-tip">把链接分享给好友，2 人成团即可发货</p>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import { getGroupDetailApi } from '@/api/group'
import { Toast } from 'vant'
import QRCode from 'qrcode'

export default {
  name: 'GroupPage',
  data () {
    return {
      loading: true,
      detail: null,
      errorText: '',
      countdownText: '',
      timer: null,
      reloading: false,
      shareVisible: false,
      shareUrl: '',
      qrDataUrl: '',
      defaultAvatar: ''
    }
  },
  computed: {
    groupNo () {
      return this.$route.query.groupNo || ''
    },
    progressPercent () {
      if (!this.detail) return 0
      const g = this.detail.group
      return Math.min(100, Math.round((g.joined_num / g.total_num) * 100))
    },
    memberList () {
      if (!this.detail) return []
      const members = (this.detail.group.members || []).slice()
      const total = this.detail.group.total_num
      while (members.length < total) {
        members.push({ nickname: '待参团', avatar: '' })
      }
      return members.slice(0, total)
    },
    isMyOrderUnpaid () {
      const o = this.detail && this.detail.me && this.detail.me.my_order
      return !!(o && o.status === 0 && o.group_status === 0)
    }
  },
  created () {
    if (!this.groupNo) {
      this.errorText = '参数错误'
      this.loading = false
      return
    }
    this.loadDetail()
  },
  beforeDestroy () {
    this.clearTimer()
  },
  methods: {
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/home')
      }
    },
    clearTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    async loadDetail () {
      this.loading = true
      try {
        const { data } = await getGroupDetailApi(this.groupNo)
        this.detail = data
        this.errorText = ''
        this.reloading = false
        if (data.group.status === 0) {
          this.startCountdown()
        } else {
          this.clearTimer()
          this.countdownText = ''
        }
        // 支付成功回跳：自动弹出分享
        if (this.$route.query.joined === '1') {
          this.openShare()
        }
      } catch (e) {
        this.detail = null
        this.errorText = '拼单不存在或已结束'
      } finally {
        this.loading = false
      }
    },
    startCountdown () {
      this.clearTimer()
      this.updateCountdown()
      this.timer = setInterval(this.updateCountdown, 1000)
    },
    updateCountdown () {
      if (!this.detail || this.detail.group.status !== 0) {
        this.clearTimer()
        return
      }
      const end = new Date(String(this.detail.group.expire_at).replace(/-/g, '/')).getTime()
      const diff = end - Date.now()
      if (diff <= 0) {
        this.countdownText = '拼单已结束'
        this.clearTimer()
        // 重新拉取，让后端把超时团标记为失败
        if (!this.reloading) {
          this.reloading = true
          this.loadDetail()
        }
        return
      }
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      this.countdownText = `剩余 ${h}时${String(m).padStart(2, '0')}分${String(s).padStart(2, '0')}秒`
    },
    joinGroup () {
      if (!this.$store.getters.token) {
        this.$router.replace(`/login?redirect=${encodeURIComponent('/group?groupNo=' + this.groupNo)}`)
        return
      }
      if (!this.detail) return
      // 跳转到订单结算台参与拼单（mode=group + groupNo，结算台按团价结算）
      this.$router.push({
        path: '/pay',
        query: {
          mode: 'group',
          groupNo: this.groupNo,
          goodsId: this.detail.goods.goods_id,
          goodsNum: 1
        }
      })
    },
    goPay () {
      const o = this.detail.me.my_order
      if (o) {
        this.goPayment({
          orderId: o.order_id,
          orderNo: o.order_no,
          amount: o.total_price,
          groupNo: this.groupNo
        })
      }
    },
    // 联系客服（与商品详情页一致：未登录先跳登录，再进入买家端聊天页）
    goService () {
      if (!this.$store.getters.token) {
        this.$router.replace(`/login?redirect=${encodeURIComponent('/chat')}`)
        return
      }
      this.$router.push('/chat')
    },
    // 跳转到收银台支付
    goPayment (data) {
      this.$router.push({
        path: '/payment',
        query: {
          orderId: data.orderId,
          orderNo: data.orderNo,
          amount: data.amount,
          isGroup: 1,
          groupNo: data.groupNo || this.groupNo
        }
      })
    },
    // 打开分享弹窗（生成链接 + 二维码）
    openShare () {
      // history 路由：分享链接为真实路径（/group?groupNo=xxx）
      this.shareUrl = `${location.origin}/group?groupNo=${this.groupNo}`
      this.shareVisible = true
      QRCode.toDataURL(this.shareUrl, { width: 200, margin: 2 })
        .then((url) => {
          this.qrDataUrl = url
        })
        .catch(() => {
          this.qrDataUrl = ''
        })
    },
    share () {
      if (!this.$store.getters.token) {
        this.$router.replace(`/login?redirect=${encodeURIComponent('/group?groupNo=' + this.groupNo)}`)
        return
      }
      this.openShare()
    },
    async copyLink () {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.shareUrl)
        } else {
          throw new Error('no clipboard')
        }
        Toast.success('链接已复制')
      } catch (e) {
        // 降级方案：模拟选中复制
        const ta = document.createElement('textarea')
        ta.value = this.shareUrl
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        Toast.success('链接已复制')
      }
    },
    toWechatShare () {
      Toast('请点击微信右上角「···」转发给好友')
    }
  }
}
</script>

<style lang="less" scoped>
.group-page {
  min-height: 100vh;
  background: #f5f6f8;
  padding-top: 46px;
  padding-bottom: 160px;

  /deep/ .van-nav-bar__arrow { color: #333; }
  /deep/ .van-nav-bar__text { color: #333; }
  /deep/ .van-nav-bar { z-index: 100; }

  .center-state {
    padding: 80px 20px;
    text-align: center;
    color: #999;
    font-size: 15px;
    .err-icon { font-size: 48px; display: block; margin-bottom: 12px; }
  }

  .status-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 16px;
    color: #fff;
    &.s0 { background: linear-gradient(90deg, #ff6b35, #ff8c42); }
    &.s1 { background: linear-gradient(90deg, #4caf50, #66bb6a); }
    &.s2 { background: linear-gradient(90deg, #999, #bbb); }
    .sb-title { font-size: 18px; font-weight: 700; }
    .sb-sub { font-size: 12px; opacity: 0.9; margin-top: 4px; }
    .sb-countdown {
      font-size: 13px;
      font-variant-numeric: tabular-nums;
      background: rgba(0, 0, 0, 0.15);
      border-radius: 14px;
      padding: 6px 12px;
    }
  }

  .goods-card {
    display: flex;
    background: #fff;
    margin: 12px;
    border-radius: 12px;
    padding: 14px;
    box-shadow: 0 0.5px 2px 0 rgba(0, 0, 0, 0.05);
    .g-img {
      width: 100px;
      height: 100px;
      border-radius: 8px;
      object-fit: cover;
      margin-right: 12px;
    }
    .g-info { flex: 1; display: flex; flex-direction: column; justify-content: space-between; }
    .g-name { font-size: 14px; color: #333; line-height: 1.4; }
    .g-price {
      .group-price { color: #ff6b35; font-size: 18px; font-weight: 700; }
      .origin-price { color: #999; font-size: 12px; text-decoration: line-through; margin-left: 8px; }
    }
  }

  .progress-card {
    background: #fff;
    margin: 0 12px;
    border-radius: 12px;
    padding: 16px 14px;
    box-shadow: 0 0.5px 2px 0 rgba(0, 0, 0, 0.05);
    .p-tit {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      font-size: 13px;
      color: #666;
      .p-num { color: #ff6b35; font-weight: 700; }
    }
    .members {
      display: flex;
      margin-top: 16px;
      gap: 18px;
      flex-wrap: wrap;
      .member {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 48px;
        .m-avatar { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; background: #f2f3f5; }
        .m-name {
          font-size: 11px;
          color: #999;
          margin-top: 6px;
          max-width: 48px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  .action-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 90;
    background: #fff;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 16px calc(14px + env(safe-area-inset-bottom));
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);

    .btn {
      border: none;
      border-radius: 22px;
      height: 44px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      width: 100%;
      &.primary { background: linear-gradient(90deg, #ff6b35, #ff8c42); color: #fff; }
      &.done { background: #e8e8e8; color: #999; }
      &:active { transform: scale(0.98); }
    }

    .action-sub-row {
      display: flex;
      gap: 10px;
      .btn {
        flex: 1;
        height: 40px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 500;
        width: auto;
      }
      .btn.share { background: #fff3e8; color: #ff6b35; border: 1px solid #ffd9bd; }
      .btn.service { background: #eef7ff; color: #1989fa; border: 1px solid #cfe5fb; }
      .btn.plain { background: #f7f8fa; color: #666; border: 1px solid #eee; }
    }
  }

  .share-box {
    padding: 8px 20px 28px;
    text-align: center;
    .share-link {
      font-size: 12px;
      color: #666;
      background: #f7f8fa;
      border-radius: 6px;
      padding: 10px;
      word-break: break-all;
      text-align: left;
      margin-bottom: 14px;
    }
    .share-actions {
      display: flex;
      gap: 10px;
      margin-bottom: 14px;
      .share-btn {
        flex: 1;
        height: 38px;
        border: none;
        border-radius: 19px;
        background: linear-gradient(90deg, #ff6b35, #ff8c42);
        color: #fff;
        font-size: 14px;
        cursor: pointer;
      }
    }
    .share-qr { width: 200px; height: 200px; border: 1px solid #eee; border-radius: 8px; }
    .share-tip { font-size: 12px; color: #999; margin-top: 12px; }
  }
}
</style>
